import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// import { likePost, commentPost } from '../redux/postSlice';
import PostCard from '../components/PostCard';
import '../styles/Profile.css';

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // const dispatch = useDispatch();
  const { userId } = useParams();
  const { user: currentUser } = useSelector(state => state.auth);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [userPosts, setUserPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const handleLike = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/posts/${postId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state with the updated post
      setUserPosts(userPosts.map(p => p._id === postId ? response.data.post : p));
      setLikedPosts(likedPosts.map(p => p._id === postId ? response.data.post : p));
    } catch (err) {
      console.error('Error liking post:', err);
    }
  };

  const handleComment = async (postId, text) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/posts/${postId}/comment`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state with the updated post
      setUserPosts(userPosts.map(p => p._id === postId ? response.data.post : p));
      setLikedPosts(likedPosts.map(p => p._id === postId ? response.data.post : p));
    } catch (err) {
      console.error('Error commenting on post:', err);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/${userId}`
        );
        setProfile(response.data);
        setIsFollowing(response.data.followers?.some(f => f._id === currentUser?._id));

        // Check if user is blocked
        const token = localStorage.getItem('token');
        const blockStatus = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/is-blocked/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsBlocked(blockStatus.data.isBlocked);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId, currentUser]);

  useEffect(() => {
    const fetchUserPosts = async () => {
      try {
        setLoadingPosts(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/posts/user/${userId}`
        );
        setUserPosts(response.data || []);
      } catch (error) {
        console.error('Error fetching user posts:', error);
        setUserPosts([]);
      } finally {
        setLoadingPosts(false);
      }
    };

    if (userId) fetchUserPosts();
  }, [userId]);

  useEffect(() => {
    const fetchLikedPosts = async () => {
      try {
        setLoadingPosts(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/posts/liked/${userId}`
        );
        setLikedPosts(response.data || []);
      } catch (error) {
        console.error('Error fetching liked posts:', error);
        setLikedPosts([]);
      } finally {
        setLoadingPosts(false);
      }
    };

    if (userId && activeTab === 'liked') fetchLikedPosts();
  }, [userId, activeTab]);

  const handleFollow = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/${userId}/follow`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsFollowing(true);
    } catch (error) {
      console.error('Error following user:', error);
    }
  };

  const handleUnfollow = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/${userId}/unfollow`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsFollowing(false);
    } catch (error) {
      console.error('Error unfollowing user:', error);
    }
  };

  const handleBlock = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/users/block/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsBlocked(response.data.isBlocked);
      alert(response.data.message);
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Error blocking user');
    }
  };

  if (loading) return <div className="loading">Loading profile...</div>;

  if (!profile?.isActive) {
    return (
      <div className="profile-container">
        <div style={{
          background: 'rgba(255, 107, 107, 0.1)',
          border: '2px solid #ff6b6b',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
          marginTop: '2rem'
        }}>
          <h2 style={{ color: '#ff6b6b', marginBottom: '1rem' }}>🚫 User Banned</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '1rem' }}>
            This user account has been banned and is no longer active.
          </p>
          <p style={{ color: '#888', marginBottom: '2rem' }}>
            You cannot interact with this account.
          </p>
          <button 
            onClick={() => navigate('/')}
            style={{
              background: '#1dd1a1',
              color: '#0f0f0f',
              border: 'none',
              padding: '0.75rem 2rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem'
            }}
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div style={{display: 'flex', gap: '1.5rem', alignItems: 'flex-start', width: '100%'}}>
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: profile?.profilePicture ? 'transparent' : 'linear-gradient(135deg, #1dd1a1, #00b894)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '3rem',
            flexShrink: 0,
            overflow: 'hidden',
            border: '3px solid #1dd1a1'
          }}>
            {profile?.profilePicture ? (
              <img src={`http://localhost:5000${profile.profilePicture}`} alt="Avatar" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
            ) : (
              `${profile?.firstName?.charAt(0)}${profile?.lastName?.charAt(0)}`
            )}
          </div>
          
          <div style={{flex: 1}}>
            <h1 style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
              {profile?.icon && (
                <img 
                  src={`http://localhost:5000${profile.icon}`} 
                  alt="icon"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #1dd1a1',
                    backgroundColor: '#1a1a1a'
                  }}
                />
              )}
              <span style={{color: profile?.nicknameColor || '#ffffff'}}>
                {profile?.firstName} {profile?.lastName}
              </span>
              {profile?.badge && <span style={{fontSize: '1.5rem'}}>{profile.badge}</span>}
            </h1>
            <p style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
              @{profile?.username}
              {profile?.isVerified && (
                <span title="✓ Official Account" style={{color: '#1dd1a1', fontSize: '1.2rem', cursor: 'help', position: 'relative'}}>✓</span>
              )}
            </p>
            {profile?.bio && <p className="bio">{profile.bio}</p>}
          </div>

          <div>
            {userId !== currentUser?._id && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {isFollowing ? (
                  <button className="follow-btn following" onClick={handleUnfollow} disabled={!profile?.isActive}>
                    {t('profile.following')}
                  </button>
                ) : (
                  <button className="follow-btn" onClick={handleFollow} disabled={!profile?.isActive}>
                    {t('profile.follow')}
                  </button>
                )}
                <button 
                  className="follow-btn"
                  onClick={() => navigate(`/messages?user=${userId}`)}
                  title="Send message"
                  disabled={!profile?.isActive}
                  style={{ opacity: profile?.isActive ? 1 : 0.5, cursor: profile?.isActive ? 'pointer' : 'not-allowed' }}
                >
                  ✉️ {t('common.sendMessage') || 'Message'}
                </button>
                <button 
                  className={`follow-btn ${isBlocked ? 'blocked' : ''}`}
                  onClick={handleBlock}
                  title={isBlocked ? 'Unblock user' : 'Block user'}
                  style={{
                    background: isBlocked ? '#ff6b6b' : 'transparent',
                    borderColor: isBlocked ? '#ff6b6b' : '#2a2a2a',
                    color: isBlocked ? '#fff' : '#999'
                  }}
                >
                  {isBlocked ? '🚫 Blocked' : '🚫 Block'}
                </button>
              </div>
            )}
            {userId === currentUser?._id && (
              <button className="follow-btn" onClick={() => navigate('/edit-profile')}>
                {t('profile.editProfile')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Posts and Likes Tabs */}
      <div style={{
        display: 'flex',
        gap: '2rem',
        marginBottom: '2rem',
        borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
        padding: '0 1rem'
      }}>
        <button
          onClick={() => setActiveTab('posts')}
          style={{
            background: 'none',
            border: 'none',
            color: activeTab === 'posts' ? '#1dd1a1' : '#888',
            fontSize: '1.1rem',
            cursor: 'pointer',
            padding: '1rem 0',
            borderBottom: activeTab === 'posts' ? '3px solid #1dd1a1' : 'none',
            marginBottom: '-2px',
            transition: 'all 0.3s ease',
            fontWeight: activeTab === 'posts' ? 'bold' : 'normal'
          }}
        >
          📝 Posts
        </button>
        <button
          onClick={() => setActiveTab('liked')}
          style={{
            background: 'none',
            border: 'none',
            color: activeTab === 'liked' ? '#1dd1a1' : '#888',
            fontSize: '1.1rem',
            cursor: 'pointer',
            padding: '1rem 0',
            borderBottom: activeTab === 'liked' ? '3px solid #1dd1a1' : 'none',
            marginBottom: '-2px',
            transition: 'all 0.3s ease',
            fontWeight: activeTab === 'liked' ? 'bold' : 'normal'
          }}
        >
          ❤️ Liked
        </button>
      </div>

      {/* Posts Section */}
      {activeTab === 'posts' && (
        <div style={{padding: '1rem'}}>
          {loadingPosts ? (
            <div style={{textAlign: 'center', padding: '2rem'}}>Loading posts...</div>
          ) : userPosts.length === 0 ? (
            <div style={{textAlign: 'center', padding: '2rem', color: '#888'}}>
              No posts yet
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
              {userPosts.map(post => (
                <PostCard 
                  key={post._id} 
                  post={post}
                  onLike={handleLike}
                  onComment={handleComment}
                  onPostDeleted={() => setUserPosts(userPosts.filter(p => p._id !== post._id))}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Liked Posts Section */}
      {activeTab === 'liked' && (
        <div style={{padding: '1rem'}}>
          {loadingPosts ? (
            <div style={{textAlign: 'center', padding: '2rem'}}>Loading liked posts...</div>
          ) : likedPosts.length === 0 ? (
            <div style={{textAlign: 'center', padding: '2rem', color: '#888'}}>
              No liked posts yet
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
              {likedPosts.map(post => (
                <PostCard 
                  key={post._id} 
                  post={post}
                  onLike={handleLike}
                  onComment={handleComment}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="profile-stats">
        <div 
          className="stat" 
          onClick={() => navigate(`/followers/${userId}`)}
          style={{cursor: 'pointer'}}
          onMouseEnter={(e) => e.currentTarget.style.color = '#1dd1a1'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
        >
          <span className="stat-label">{t('profile.followers')}</span>
          <span className="stat-value">{profile?.followers?.length || 0}</span>
        </div>
        <div 
          className="stat"
          onClick={() => navigate(`/following/${userId}`)}
          style={{cursor: 'pointer'}}
          onMouseEnter={(e) => e.currentTarget.style.color = '#1dd1a1'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
        >
          <span className="stat-label">{t('profile.following')}</span>
          <span className="stat-value">{profile?.following?.length || 0}</span>
        </div>
      </div>
    </div>
  );
};

export default Profile;
